import { InjectionToken, Provider } from '@angular/core';

/**
 * Configuration for Property Wizard module
 */
export interface WizardConfig {
  /** Base API URL */
  apiUrl: string;

  /** Default language for the wizard */
  defaultLanguage?: 'pl' | 'en';

  /** File upload configuration */
  fileUpload?: {
    /** Direct upload endpoint relative to apiUrl (default: 'files/upload'). Used in production. */
    uploadEndpoint?: string;
    /** Token endpoint for development (fetches upload token before uploading) */
    tokenEndpoint?: string;
    /** Test token for local development (X-Test-Token header) */
    testToken?: string;
  };

  /** Theme configuration */
  theme?: {
    preset?: 'default' | 'compact';
    /** CSS custom properties to override */
    variables?: Record<string, string>;
  };

  /** Event callbacks */
  onComplete?: (result: WizardResult) => void;
  onCancel?: () => void;
}

/**
 * Result returned when wizard completes
 */
export interface WizardResult {
  propertyId?: string;
  data: unknown;
}

/**
 * InjectionToken for wizard configuration
 */
export const WIZARD_CONFIG = new InjectionToken<WizardConfig>('WIZARD_CONFIG');

/**
 * Options for providing wizard configuration
 */
export interface ProvideWizardOptions {
  apiUrl: string;
  defaultLanguage?: 'pl' | 'en';
  fileUpload?: {
    uploadEndpoint?: string;
    tokenEndpoint?: string;
    testToken?: string;
  };
  theme?: {
    preset?: 'default' | 'compact';
    variables?: Record<string, string>;
  };
  onComplete?: (result: WizardResult) => void;
  onCancel?: () => void;
}

/**
 * Provides Property Wizard configuration.
 *
 * Two upload modes:
 * - **Direct upload** (production): set `uploadEndpoint` or leave default ('files/upload')
 * - **Token-based** (development): set `tokenEndpoint` + `testToken`
 *
 * If `tokenEndpoint` is set, token-based mode is used. Otherwise direct upload.
 */
export function providePropertyWizard(options: ProvideWizardOptions): Provider[] {
  const config: WizardConfig = {
    apiUrl: options.apiUrl,
    defaultLanguage: options.defaultLanguage ?? 'pl',
    fileUpload: {
      uploadEndpoint: options.fileUpload?.uploadEndpoint ?? 'files/upload',
      tokenEndpoint: options.fileUpload?.tokenEndpoint,
      testToken: options.fileUpload?.testToken,
    },
    theme: options.theme,
    onComplete: options.onComplete,
    onCancel: options.onCancel,
  };

  return [
    {
      provide: WIZARD_CONFIG,
      useValue: config,
    },
  ];
}
